import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {PortalBaseComponent} from "../../../../core/components/portal-base/portal-base.component";
import {FormioCustomComponent} from "@formio/angular";

import * as Highcharts from 'highcharts/highcharts';
import {FeatureFlagsService} from "../../../../core/services/feature-flag/feature-flags.service";
import {PortalService} from "../../../../core/services/portal/portal.service";
import {ReportService} from "../../../../core/services/report/report.service";
import {UtilService} from "../../../../core/services/util/util.service";
import {ReportUtilService} from "../../../../core/services/report/report-util.service";
import {ResizeObserver} from '@juggle/resize-observer';

@Component({
  selector: 'sb-portal-chart,sb-dynamic-portal-chart,sb-dynamic-portal-report,sb-portal-report,sb-portal-public-report',
  templateUrl: './portal-chart.component.html',
  styleUrls: ['./portal-chart.component.scss']
})
export class PortalChartComponent extends PortalBaseComponent implements FormioCustomComponent<string>, OnInit, AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('json', {static: false}) jsonElement?: ElementRef;

  @ViewChild('chart', {static: false}) chartElement?: ElementRef;

  @Input()
  disabled: boolean;

  @Input()
  value: string;

  @Output()
  valueChange: EventEmitter<string> = new EventEmitter<string>();

  @Input()
  Chart: string;

  @Input()
  parameters: any;

  @Input()
  parametersJSON: string;

  @Input()
  preview: boolean;

  @Input()
  debug: boolean = false;

  // used to determine if the component is visible
  @ViewChild("portalChartComponent")
  portalChartComponent: ElementRef;

  private refreshChartSubscription: any;

  // Receives event from items such as tabs
  private resizeObserver: ResizeObserver;

  // Tracks whether we have performed the initial load or not for lazy loading
  hasInitialDataLoadOccurred: boolean = false;

  // Data for Highcharts is returned from the chart definition. You have to supply a series to start with or
  // Highcharts will not update correctly when new data is supplied, e.g. when a different chart is chosen in edit mode.
  data: { options?: any, data?: any[] } = {
    options: {
      series: [{
        data: []
      }]
    },
    data: []
  };

  // It is not possible to reset the options in the HighCharts component as they accumulate. Hence we have to destroy and
  // rebuild the component on every change. This flag tells angular which version of the highcharts component to display.
  toggleChart: boolean = false;

  // Highcharts Angular component settings.
  Highcharts = Highcharts;
  chartConstructor = 'chart'; // optional string, defaults to 'chart'
  updateFlag = false; // optional boolean, set to true when data updated

  // Key into the ReportService for drill down purposes
  reportComponentKey: string;

  constructor(private reportService: ReportService,
              private reportUtilService: ReportUtilService,
              private featureFlagsService: FeatureFlagsService,
              private utilService: UtilService,
              public portalService: PortalService,
              public hostElement: ElementRef,
              private zone: NgZone) {
    super(portalService, hostElement);
  }

  async ngOnInit() {
    this.debug = this.featureFlagsService.isOn("report-debugging-display");
    this.reportComponentKey = this.utilService.generateUUID();

    this.resizeObserver = new ResizeObserver((entries, observer) => {
      this.zone.run(() => {
        // If the item becomes visible after a form resize event (happens when a tab is navigated to)
        // and the data has not been loaded yet, then do so.
        // Otherwise just resize the form
        if (!this.hasInitialDataLoadOccurred && this.isVisibleOnPage()) {
          this.renderChart();
        }
      });
    });
  }

  async ngAfterViewInit() {
    this.registerMessageBusListeners();

    this.resizeObserver.observe(this.portalChartComponent.nativeElement);

    // We need this to occur in a timeout to give the form time to display the Tabs
    setTimeout(() => {
      if (this.Chart && this.isVisibleOnPage()) {
          this.renderChart();
      }
    });
  }

  /**
   * Checks if the component is currently visible on the page.
   *
   * @returns {boolean} True if the component is currently visible on the page, otherwise false.
   */
  isVisibleOnPage(): boolean {
    let isVisible = this.portalChartComponent.nativeElement.offsetParent !== null;
    return isVisible;
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (this.Chart && this.hasInitialDataLoadOccurred) {
      await this.renderChart();
    }
  }

  chartInstance(chart) {
  }

  updateChange(event) {
  }

  ngOnDestroy() {
    this.reportService.removeReport(this.reportComponentKey);
    if (this.refreshChartSubscription) { this.refreshChartSubscription.unsubscribe(); }
    if (this.resizeObserver) { this.resizeObserver.unobserve(this.portalChartComponent.nativeElement); }
  }

  async refresh() {
    await this.renderChart();
  }

  chartWrapperClassName() {
      let className = 'dynamic-portal-chart';

      if (this?.data?.options?.servicely?.className !== undefined) {
        className = this?.data?.options?.servicely?.className;
      }

      return className;
  }

  debugStyle() {
    return this.debug ? '' : 'display: none';
  }

  private async renderChart() {

    if (this.Chart) {

      const requestOptions = {
        _request_type: "getReportContent",
        _chart_id: this.Chart,
        _context: {}
      };

      try {
        // Call ReportController which in turn calls Report script library and returns HighCharts config
        const response = await (window as any).WS.controller("ReportController", requestOptions);

        const responseData = response.data || response;

        if (responseData.error || responseData._error) {
          console.error(responseData.error || responseData._error);
        } else {
          const chartContent = responseData;
          if (chartContent && typeof chartContent === "object" && chartContent.options && chartContent.data) {
            this.reifyFunctions(chartContent.options);

            // Supplying data explicitly in the chart definition
            if (chartContent.data && chartContent.data.length > 0) {
              chartContent.options.series = chartContent.data;
            }

            // Call the before function on the chart if it exists.
            if (chartContent.options.servicely && chartContent.options.servicely.before) {
              chartContent.options.servicely.before.call(chartContent.options, this.reportUtilService, Highcharts);
            }

            // Store the data - note that highcharts will combine these options with the existing ones so we have to
            // destroy the old component and build a new one.
            this.data = {
              options: chartContent.options,
              data: chartContent.data,
            };

            this.reportService.addReport(this.reportComponentKey, this.data);

            this.toggleChart = !this.toggleChart;
            this.updateFlag = true;
            this.updateDebugDisplay();
          }
        }
        this.hasInitialDataLoadOccurred = true;
      } catch (e) {
        console.error('PortalChartComponent.renderChart', e);
      }
    }
  }

  private reifyFunctions(obj: any) {
    const that = this;
    Object.getOwnPropertyNames(obj).forEach(function (name) {
      const functionMarker = 'function:';
      const value = obj[name];
      if (typeof(value) === 'string' && value.substring(0, functionMarker.length) === functionMarker) {
        obj[name] = eval('(' + value.substring(functionMarker.length) + ')');
      } else if (value && typeof(value) === 'object') {
        that.reifyFunctions(value);
      } else if (value && Array.isArray(value)) {
        value.forEach(function(element) {
          that.reifyFunctions(element);
        })
      }
    });
  }

  private updateDebugDisplay() {
    if (this.jsonElement) {
      this.jsonElement.nativeElement.innerHTML = '';
      this.jsonElement.nativeElement.appendChild(document.createTextNode(JSON.stringify(this.data, null, 4)));
    }
  }

  private registerMessageBusListeners() {
    if (this.refreshChartSubscription == null) {
      // Put in a message bus subscription for refreshes from Navigate.refresh()
      this.refreshChartSubscription = (window as any).MessageBus.subscribe(
        (window as any).MessageBus.CHANNELS.REFRESH_EVENT,
        'TENANCY_FILTER_CHANGE',
        (data: any, envelope: any) => {
          this.refresh();
        });
    }
  }
}
